import React, { useEffect, useState } from "react";
import Select from "react-select";
import { connect } from "react-redux";
import { Col, Row } from "react-bootstrap";
import { TbArrowsExchange } from "react-icons/tb";
import { Controller, useForm } from "react-hook-form";

// Redux actions and constants
import { storeActions } from "../../redux/actions";
import { mergeDuplicateStoresActions } from "../../redux/actions/mergeDuplicateStores.actions";
import { groupsConstants } from "../../../shared-screens/constants";

// Component and helper imports
import { COLORS, storesConstants } from "../../constants";
import { useSpinner, UTCToDateTimeFormat } from "../../../../shared";
import MergeDublicateStoreLogsTable from "./mergeDublicateStoreLogsTable";

// Custom styles for react-select
const styleReactSelect = (error) => ({
  control: (styles) => ({
    ...styles,
    borderRadius: "6px",
    minHeight: "2.3rem",
    borderColor: error ? COLORS.SCARLET : COLORS.ASH_GREY,
    "&:hover": { borderColor: error ? COLORS.SCARLET : COLORS.ASH_GREY },
    boxShadow: "none",
  }),
});

const MergeDuplicateStores = (props) => {
  const {
    getAllBrands,
    getAllStoresByBrandId,
    storesReducer: { allBrands, allStoresByBrandId },
    mergeDublicateStores,
  } = props;

  const {
    reset,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedOldStore, setSelectedOldStore] = useState(null);
  const [selectedNewStore, setSelectedNewStore] = useState(null);

  const spinner = useSpinner();

  useEffect(() => {
    getAllBrands(true);
  }, [getAllBrands]);

  const handleBrandSelect = (brand) => {
    setSelectedOldStore(null);
    setSelectedNewStore(null);
    setSelectedBrand(brand);
    getAllStoresByBrandId(brand.id);
  };

  const filteredNewStoreOptions = selectedOldStore
    ? allStoresByBrandId?.filter((loc) => loc.id !== selectedOldStore.id)
    : allStoresByBrandId;

  const filteredOldStoreOptions = selectedNewStore
    ? allStoresByBrandId?.filter((loc) => loc.id !== selectedNewStore.id)
    : allStoresByBrandId;

  const handleMergeStores = () => {
    if (selectedOldStore && selectedNewStore) {
      mergeDublicateStores(
        selectedOldStore.id,
        selectedNewStore.id,
        getAllStoresByBrandId,
        selectedBrand.id
      );
      reset(); // Reset the form on successful merge
      setSelectedOldStore(null);
      setSelectedNewStore(null);
    }
  };

  return (
    <div className="container-fluid py-3">
      <div className="notification-form card shadow px-4 pt-2 mb-3">
        <div className="card-body">
          <form onSubmit={handleSubmit(handleMergeStores)}>
            <div className="row new-user ms-0 me-0">
              <div className="col-12 ps-0">
                <h5>Merge Stores</h5>
              </div>
            </div>

            <div className="form-group mb-3 row">
              <div className="col-md-5 col-8">
                <h6 className="mt-2 col-md-3 col-4 text-truncate">Brand:</h6>
                <Controller
                  name="brandId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={allBrands}
                      placeholder="Select Brand"
                      getOptionLabel={(e) => e.name}
                      getOptionValue={(e) => e.id}
                      styles={styleReactSelect(errors.brandId)}
                      isLoading={spinner(storesConstants.FETCH_ALL_BRANDS)}
                      onChange={(brand) => handleBrandSelect(brand)}
                      isDisabled={spinner(storesConstants.FETCH_ALL_BRANDS)}
                      className="store-brandDropdown"
                    />
                  )}
                  rules={{ required: true }}
                />
              </div>
            </div>

            {selectedBrand && (
              <div className="form-group mb-3">
                {/* Row for Labels */}
                <Row className="d-flex justify-content-center">
                  <Col md={5} className="d-flex flex-column ">
                    <h6 className="mt-2">Old Store:</h6>
                  </Col>
                  <Col md={2} className="d-flex justify-content-center">
                    {/* Empty space for alignment */}
                  </Col>
                  <Col md={5} className="d-flex flex-column">
                    <h6 className="mt-2">New Store:</h6>
                  </Col>
                </Row>

                {/* Row for Fields and Arrow */}
                <Row className="d-flex justify-content-center align-items-start">
                  <Col md={5} className="d-flex flex-column">
                    <Controller
                      name="oldStore"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          value={selectedOldStore}
                          options={filteredOldStoreOptions}
                          getOptionLabel={(e) => e.name}
                          getOptionValue={(e) => e.id}
                          onChange={(store) => setSelectedOldStore(store)}
                          placeholder="Select Old Store"
                          styles={styleReactSelect(errors.oldStore)}
                          isLoading={spinner(groupsConstants.GROUPS_DATA)}
                          isDisabled={spinner(groupsConstants.GROUPS_DATA)}
                        />
                      )}
                      rules={{ required: true }}
                    />
                    {selectedOldStore && (
                      <span className="my-2">
                        <strong>Store Created Date: </strong>{" "}
                        {UTCToDateTimeFormat(selectedOldStore.createdAt)}
                      </span>
                    )}
                  </Col>

                  {/* Center Arrow */}
                  <Col
                    md={2}
                    className="d-flex justify-content-center align-items-center"
                  >
                    <TbArrowsExchange size={25} className="h1" />{" "}
                    {/* Increased size */}
                  </Col>

                  <Col md={5} className="d-flex flex-column">
                    <Controller
                      name="newStore"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={filteredNewStoreOptions}
                          value={selectedNewStore}
                          getOptionLabel={(e) => e.name}
                          getOptionValue={(e) => e.id}
                          onChange={(store) => setSelectedNewStore(store)}
                          placeholder="Select New Store"
                          styles={styleReactSelect(errors.newStore)}
                          isDisabled={!selectedOldStore}
                          isLoading={spinner(groupsConstants.GET_ALL_LOCATIONS)}
                        />
                      )}
                      rules={{ required: true }}
                    />
                    {selectedNewStore && (
                      <span className="my-2">
                        <strong>Store Created Date: </strong>{" "}
                        {UTCToDateTimeFormat(selectedNewStore.createdAt)}
                      </span>
                    )}
                  </Col>
                </Row>
              </div>
            )}

            <div className="form-group mb-3 d-flex justify-content-end">
              <button
                type="button"
                onClick={handleMergeStores}
                data-testId="merge-btn"
                className="btn user-permission-button px-4"
                disabled={!selectedOldStore || !selectedNewStore}
              >
                Merge
              </button>
            </div>
          </form>
        </div>
      </div>
      <MergeDublicateStoreLogsTable />
    </div>
  );
};

const mapStateToProps = (state) => ({
  storesReducer: state.storesReducer,
});

const mapDispatchToProps = (dispatch) => ({
  getAllStoresByBrandId: (brandId) =>
    dispatch(storeActions.getAllStoresByBrandId(brandId)),
  getAllBrands: (isActiveOnly) =>
    dispatch(storeActions.getAllBrands(isActiveOnly)),
  mergeDublicateStores: (oldStoreId, newStoreId, callback, brandId) => {
    dispatch(
      mergeDuplicateStoresActions.mergeDublicateStores(
        oldStoreId,
        newStoreId,
        callback,
        brandId
      )
    );
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MergeDuplicateStores);
